/**
 * Smart API: read-only list endpoints consumed by Consumption Events,
 * Restock Events, Device Status, and Inventory.
 *
 * (2026-04-24, Jason Cox) Extracted from unifiedApiService.ts so each
 * feature's request shape, response shape, and wrapping logic lives next
 * to its types in one small file. SmartApiAdapter now delegates.
 *
 * Endpoints:
 *   POST /scm/v1/listDeliverTicket     (Consumption Events)
 *   POST /scm/v1/listTransferTicket    (Restock Events)
 *   POST /md/v1/listSmartBox           (Device Status + shared dropdown)
 *   POST /md/v1/listBaseRfid           (Inventory)
 */
import { fetchWithRetry } from '../../utils/fetchWithRetry';
import type { ApiConfig } from '../apiConfig';

// ---------- Delivery Tickets (Consumption Events) ----------

export interface DeliveryTicketFilters {
  page?: number;
  pageSize?: number;
  startTime?: string;   // YYYY-MM-DD
  endTime?: string;     // YYYY-MM-DD
  sku?: string;
  rfid?: string;
  docNum?: string;
  wName?: string;
  smartboxId?: number;
  sort?: 1 | 2;         // 1=asc, 2=desc (backend convention)
  field?: string;
}

export interface DeliveryTicketRow {
  id: number;
  docNum: string;
  createAt: string | number;
  docDate: string | number;
  sku?: string;
  productName?: string;
  unitName?: string;
  rfid?: string;
  price: string | number;
  qty: number;
  total: string | number;
  sName?: string;
  workerName?: string;
  vendor?: string;
  client?: string;
  smartboxId?: number;
  clientId?: number;
  vendorId?: number;
  itemId?: number;
  lockUser?: string;
  image?: string;
  clientItemCode?: string;
  isInvoice?: number;
  headId?: number;
}

export interface DeliveryTicketResult {
  data: DeliveryTicketRow[];
  total: number;
  currentPage: number;
  totalPages: number;
}

// ---------- Transfer Tickets (Restock Events) ----------

export interface TransferTicketFilters {
  page?: number;
  pageSize?: number;
  startTime?: string;
  endTime?: string;
  sku?: string;
  rfid?: string;
  docNum?: string;
  wName?: string;
  smartboxId?: number;
  sort?: 1 | 2;
  field?: string;
}

export interface TransferTicketRow {
  id: number;
  docNum: string;
  createAt: string | number;
  itemId?: number;
  itemName?: string;
  sku?: string;
  image?: string;
  note?: string;
  onhand: number;
  type?: number;
  smartboxId?: number;
  smartboxName?: string;
  workcenterId?: number;
  workcenterName?: string;
  lockUser?: string;
  price: string | number;
  total: string | number;
  companyCode?: string;
  companyName?: string;
  clientItemCode?: string;
  headId?: number;
}

export interface TransferTicketResult {
  data: TransferTicketRow[];
  total: number;
  currentPage: number;
  totalPages: number;
}

// ---------- Smartbox status / list ----------

export interface SmartboxStatusFilters {
  page?: number;
  pageSize?: number;
  name?: string;
  wName?: string;
  status?: 0 | 1;
}

export interface SmartboxStatusRow {
  id: number;
  code: string;
  name: string;
  workCenterId: number;
  wName: string;
  status: 0 | 1;
  online?: 0 | 1;
  dataAt: number | string;
  rfidCount: number;
  readerAlert?: 0 | 1;
  updateAt?: number | string;
  lastUpdate?: number | string;
  isError?: 0 | 1;
}

export interface SmartboxStatusResult {
  data: SmartboxStatusRow[];
  total: number;
  currentPage: number;
  totalPages: number;
}

// ---------- Base RFID (Inventory) ----------

export interface BaseRfidFilters {
  page?: number;
  pageSize?: number;
  smartboxId?: number;
  status?: number;
  sku?: string;
  itemName?: string;
}

export interface BaseRfidRow {
  id: number;
  rfid: string;
  status: number;
  sku?: string;
  Name?: string;
  sName?: string;
  packageQty?: number;
  manageType?: number;
  InDate?: string;
  [key: string]: any;
}

export interface BaseRfidResult {
  data: BaseRfidRow[];
  total: number;
  currentPage: number;
  totalPages: number;
}

// ---------- implementations ----------

export async function getDeliveryTicketList(
  config: ApiConfig,
  filters: DeliveryTicketFilters = {},
): Promise<DeliveryTicketResult> {
  const body: Record<string, any> = {
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? 50,
    sort: filters.sort ?? 2,
    field: filters.field ?? 'createAt',
  };
  if (filters.startTime) body.startTime = filters.startTime;
  if (filters.endTime) body.endTime = filters.endTime;
  if (filters.sku) body.sku = filters.sku;
  if (filters.rfid) body.rfid = filters.rfid;
  if (filters.docNum) body.docNum = filters.docNum;
  if (filters.wName) body.wName = filters.wName;
  if (filters.smartboxId) body.smartboxId = filters.smartboxId;
  // (2026-04-23) listDeliverTicket is slow on hq-apps-testing -- the COUNT(*)
  // over joined tables of scm_deliveryticket (1.15M rows) takes tens of
  // seconds even for narrow date ranges. Widen the timeout accordingly.
  const response = await fetchWithRetry(
    `${config.baseUrl}/scm/v1/listDeliverTicket`,
    {
      method: 'POST',
      headers: config.authHeaders,
      body: JSON.stringify(body),
    },
    { maxRetries: 1, timeout: 90000 },
  );
  const data = await response.json();
  if (data.code !== 0) throw new Error(data.msg || 'listDeliverTicket failed');
  const payload = data.data || {};
  return {
    data: (payload.data || []) as DeliveryTicketRow[],
    total: payload.total || 0,
    currentPage: payload.current_page || body.page,
    totalPages: payload.last_page || 1,
  };
}

export async function getTransferTicketList(
  config: ApiConfig,
  filters: TransferTicketFilters = {},
): Promise<TransferTicketResult> {
  const body: Record<string, any> = {
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? 50,
    sort: filters.sort ?? 2,
    field: filters.field ?? 'createAt',
  };
  if (filters.startTime) body.startTime = filters.startTime;
  if (filters.endTime) body.endTime = filters.endTime;
  if (filters.sku) body.sku = filters.sku;
  if (filters.rfid) body.rfid = filters.rfid;
  if (filters.docNum) body.docNum = filters.docNum;
  if (filters.wName) body.wName = filters.wName;
  if (filters.smartboxId) body.smartboxId = filters.smartboxId;
  const response = await fetchWithRetry(
    `${config.baseUrl}/scm/v1/listTransferTicket`,
    {
      method: 'POST',
      headers: config.authHeaders,
      body: JSON.stringify(body),
    },
    { maxRetries: 1, timeout: 90000 },
  );
  const data = await response.json();
  if (data.code !== 0) throw new Error(data.msg || 'listTransferTicket failed');
  const payload = data.data || {};
  return {
    data: (payload.data || []) as TransferTicketRow[],
    total: payload.total || 0,
    currentPage: payload.current_page || body.page,
    totalPages: payload.last_page || 1,
  };
}

export async function getSmartboxStatusList(
  config: ApiConfig,
  filters: SmartboxStatusFilters = {},
): Promise<SmartboxStatusResult> {
  const body: Record<string, any> = {
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? 50,
    // (2026-04-23) The backend defaults to `s.status ASC` which pushes every
    // offline box to the top and hides online ones on later pages. Ask for
    // DESC so online comes first -- matters for both Device Status's
    // single-page view and the Consumption/Restock Events SmartBox dropdown
    // that caps at 500 rows.
    sort: 2,
    field: 'status',
  };
  if (filters.name) body.name = filters.name;
  if (filters.wName) body.wName = filters.wName;
  if (filters.status === 0 || filters.status === 1) body.status = filters.status;
  const response = await fetchWithRetry(
    `${config.baseUrl}/md/v1/listSmartBox`,
    {
      method: 'POST',
      headers: config.authHeaders,
      body: JSON.stringify(body),
    },
    { maxRetries: 1, timeout: 15000 },
  );
  const data = await response.json();
  if (data.code !== 0) throw new Error(data.msg || 'listSmartBox failed');
  const payload = data.data || {};
  return {
    data: (payload.data || []) as SmartboxStatusRow[],
    total: payload.total || 0,
    currentPage: payload.current_page || body.page,
    totalPages: payload.last_page || 1,
  };
}

export async function getBaseRfidList(
  config: ApiConfig,
  filters: BaseRfidFilters = {},
): Promise<BaseRfidResult> {
  const body: Record<string, any> = {
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? 500,
    sort: 0,
    field: 'id',
  };
  if (typeof filters.smartboxId === 'number') body.smartboxId = filters.smartboxId;
  if (typeof filters.status === 'number') body.status = filters.status;
  if (filters.sku) body.sku = filters.sku;
  if (filters.itemName) body.itemName = filters.itemName;
  const response = await fetchWithRetry(
    `${config.baseUrl}/md/v1/listBaseRfid`,
    {
      method: 'POST',
      headers: config.authHeaders,
      body: JSON.stringify(body),
    },
    { maxRetries: 1, timeout: 20000 },
  );
  const data = await response.json();
  // Smart API: success code is 0 or 200 depending on the route version.
  if (data.code !== 0 && data.code !== 200) {
    throw new Error(data.msg || 'listBaseRfid failed');
  }
  const payload = data.data || {};
  const rows = Array.isArray(payload.data) ? payload.data
    : Array.isArray(payload) ? payload : [];
  return {
    data: rows as BaseRfidRow[],
    total: payload.total || rows.length,
    currentPage: payload.current_page || body.page,
    totalPages: payload.last_page || 1,
  };
}
